"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { getBuildYourPackLinkProps } from "@/lib/pageLinks";
import { PACK_TIERS, PACK_DISCOUNT_PREVIEW_ENABLED } from "@/lib/packPricing";
export default function PackPromo() {
  if (!PACK_DISCOUNT_PREVIEW_ENABLED) {
    return null;
  }
  return <section aria-labelledby="pack-promo-heading" className="bg-[#f8fafb] px-4 pt-0 pb-12 sm:px-6 md:pb-16 lg:px-8">
      <div className="mx-auto max-w-[1440px]">
        <div className="relative overflow-hidden rounded-[30px] border border-[#dbe6eb] bg-[#eef5f8]">
          {/* Subtle blue atmosphere matching the page style */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-32 h-[360px] w-[360px] rounded-full bg-[#159fdf]/10 blur-[100px]" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-36 left-[20%] h-[300px] w-[420px] rounded-full bg-[#087db3]/[0.05] blur-[110px]" />

          <div className="relative z-10 flex flex-col gap-8 px-6 py-8 sm:px-10 sm:py-10 lg:flex-row lg:items-center lg:justify-between lg:px-14 lg:py-12">
            {/* Left side: Eyebrow, Heading, Supporting text, CTA button */}
            <div className="max-w-[560px]">
              <div className="mb-4 flex items-center gap-3">
                <span aria-hidden="true" className="h-px w-8 bg-[#159fdf]" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#1479a7]">
                  Custom Research Pack
                </p>
              </div>

              <h2 id="pack-promo-heading" className="text-[clamp(2rem,3.4vw,3.25rem)] font-semibold leading-[0.98] tracking-[-0.05em] text-[#0a1218]">
                Make room for more.
              </h2>

              <p className="mt-4 text-sm leading-6 text-[#56636a] sm:text-base">
                Choose your research products and strengths in one place.
              </p>

              <div className="mt-6 sm:mt-7">
                <Link {...getBuildYourPackLinkProps()} className="group inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[#087db3] px-7 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#066d9d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087db3] focus-visible:ring-offset-2 sm:w-auto">
                  <span>Build your pack</span>
                  <ArrowRight className="size-4 transition-transform duration-200 motion-reduce:transform-none group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right side: 3 tier cards */}
            <div className="w-full shrink-0 lg:max-w-[520px]">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-3.5" role="list" aria-label="Quantity discount tiers">
                {PACK_TIERS.map(tier => <div key={tier.discountPercent} role="listitem" className="relative flex flex-col justify-between rounded-[18px] border border-[#dbe6eb] bg-white p-4 transition-all duration-200 hover:border-[#bcd4de] sm:p-4.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#1479a7]">
                      {`${tier.minVials}+ eligible vials`}
                    </span>

                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-2xl font-semibold tracking-[-0.04em] text-[#0a1218] sm:text-3xl">
                        {tier.discountPercent}%
                      </span>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#56636a]">
                        off
                      </span>
                    </div>
                  </div>)}
              </div>

              {/* Fine-print disclaimer line */}
              <p className="mt-3 text-center text-[11px] leading-5 text-[#718087] sm:text-left">
                <span className="font-medium text-[#56636a]">Final discount is confirmed in your cart.</span>
                {" · Research use only."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}